import { ElementType, Fragment, useContext, useEffect } from 'react';

import { ETypeSector, Partner, PartnerApi, PartnerDetails, partnerTypeNames, PartnerTypes } from 'apis/PartnerApi';
import { SuppliedByDropdown } from 'components/LicenseGMetrixTableDialog/SuppliedByDropdown/SuppliedByDropdown';
import { useLicenseDefaultLookupSearchFields } from 'components/GMetrixTable/gmetrixTableSearchFields';
import { GMetrixComboBoxList } from 'components/GMetrixComboBoxList/GMetrixComboBoxList';
import { LicenseGMetrixTable } from 'components/LicenseGMetrixTable/LicenseGMetrixTable';
import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { GMetrixStepper } from 'components/GMetrixStepper/GMetrixStepper';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { License, LicenseApi, SupplierOption } from 'apis/LicenseApi';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { FormattedMessage, useIntl } from 'react-intl';
import { usePartners } from 'pages/partners/context';
import { useNotification } from 'app/notifications';
import { emailStringValidation } from 'utils';
import { Col, Row } from 'react-bootstrap';
import { Country } from 'apis/CountryApi';
import { StateApi } from 'apis/StateApi';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

import 'pages/partners/components/PartnerForm/styles/partner-form.scss';

type PartnerFormProps = {
    ActionsWrapper?: ElementType;
    ContentWrapper?: ElementType;
    details: PartnerDetails;
    onClose?: () => void;
    onSuccess?: (partner: Partner) => void;
}

const defaultPartner: PartnerDetails = {
    Active: true,
    Address1: '',
    Address2: '',
    City: '',
    ContactEmail: '',
    Country: { Id: 0, Name: '' },
    CustomerIds: '',
    CustomerIDString: '',
    DateCreate: new Date(), // Getting set by db proc
    DateModified: new Date(), // Getting set by db proc
    DisabledDetailsBtn: false,
    IconURL: '',
    ID: 0,
    Id: 0,
    Language: { Id: 0, Name: '' },
    Name: '',
    OperatingCountries: [],
    PartnerType: PartnerTypes.SuperAdmin, //This gets changed immediately when moved to create form.
    Phone: '',
    Policy: '',
    Sector: null,
    SectorName: '',
    State: { Id: 0, Name: '' },
    StateID: 0,
    UserEmailList: '',
    Zip: ''
};

enum PartnerCreateStep {
    Details,
    Licenses,
    Submit
}

export const NewPartnerForm = (props: PartnerFormProps) => {

    const { ActionsWrapper = Fragment, ContentWrapper = Fragment, details, onClose, onSuccess } = props;

    const { formatMessage } = useIntl();
    const licenseDefaultLookupSearchFields = useLicenseDefaultLookupSearchFields();
    const sectors = [
        { Id: ETypeSector.K12, Name: formatMessage({ id: 'k12_education.eccc589e', defaultMessage: 'K12 Education' }) },
        { Id: ETypeSector.Government, Name: formatMessage({ id: 'government.28df1366', defaultMessage: 'Government' }) },
        { Id: ETypeSector.Commercial, Name: formatMessage({ id: 'commercial.431dfb1b', defaultMessage: 'Commercial' }) },
        { Id: ETypeSector.HigherEducation, Name: formatMessage({ id: 'higher_education.64009dd3', defaultMessage: 'Higher Education' }) }
    ];

    const [state, setState] = useSuperState({
        contactEmailIsValid: true,
        currentOperatingCountryList: [] as Country[],
        formDetails: defaultPartner as PartnerDetails,
        homeCountryIsValid: true,
        languageIsValid: true,
        licenses: [] as License[],
        loadingGridData: true,
        operatingCountries: [] as Country[],
        organizationIsValid: true,
        searchTerm: '',
        sectorIsValid: true,
        selectedLicenses: [] as License[],
        stateIsValid: true,
        step: PartnerCreateStep.Details as PartnerCreateStep
    });
    const { currentOperatingCountryList, formDetails, homeCountryIsValid, languageIsValid, licenses, loadingGridData,
        operatingCountries, searchTerm, sectorIsValid, selectedLicenses, stateIsValid, step } = state;

    const stepItems = [
        { isValid: undefined, label: formatMessage({ id: 'details.7799b923', defaultMessage: 'Details' }) },
        { isValid: undefined, label: formatMessage({ id: 'licenses.11b1c36a', defaultMessage: 'Licenses' }) }
    ];

    const { highestRole } = useApp();
    const { countries, languages, partner, setStates, states } = usePartners();
    const { permissions } = usePartners();
    const { catchWithToast, createToast } = useNotification();

    useEffect(() => {

        setState({ formDetails: { ...formDetails, PartnerDetails: details.PartnerDetails, PartnerType: details.PartnerType } });
        if (details.PartnerType !== PartnerTypes.TestingCenter) {

            const loadCountries = async () => {

                try {

                    const operatingCountries = await PartnerApi.GetPartnerOperatingCountries(details.ID);
                    setState({ currentOperatingCountryList: operatingCountries, operatingCountries: filterOutActiveCountries(operatingCountries) });

                } catch (e) {

                    catchWithToast(formatMessage({ id: 'failed_to_load_operating_countries.4a471edd', defaultMessage: 'Failed to load operating countries.' }))(e);

                }

            };
            loadCountries();

        }

    }, []);

    const handleStepperChange = async (nextStep: number) => {

        switch (true) {

            case step === PartnerCreateStep.Details: {

                if (validateNewPartnerDetails()) {

                    setState({ step: PartnerCreateStep.Licenses });

                }
                break;

            }
            case step === PartnerCreateStep.Licenses: {

                if (nextStep === PartnerCreateStep.Details) {

                    setState({ step: nextStep });
                    return;

                }

                for (let i = 0; i < selectedLicenses.length; i++) {

                    const selectedLicense = selectedLicenses[i];
                    const updatedLicense = licenses.find(license => license.Id === selectedLicense.Id);
                    if (updatedLicense) {

                        selectedLicense.SuppliedBy = updatedLicense.SuppliedBy;
                        selectedLicense.SuppliedByName = updatedLicense.SuppliedByName;

                    }

                }
                if (selectedLicenses.length === 0) {

                    createToast(formatMessage({ id: 'no_licenses_have_been_selected.499b5a69', defaultMessage: 'No licenses have been selected.' }), 'danger');
                    break;

                }
                const newPartner = await handleCreatePartnerClick();
                if (newPartner && onSuccess) {

                    onSuccess(newPartner);

                }
                break;

            }

        }

    };

    useEffect(() => {

        if (licenses.length === 0) {

            const loadLicenses = async () => {

                try {

                    const val = await LicenseApi.GetAll(1, 0, highestRole.partnerId);
                    setState({ licenses: val, loadingGridData: false });

                } catch (e) {

                    catchWithToast(formatMessage({ id: 'failed_to_load_licenses.57f5831a', defaultMessage: 'Failed to load licenses.' }))(e);
                    setState({ loadingGridData: false });

                }

            };
            loadLicenses();

        }

    }, []);

    const validateNewPartnerDetails = () => {

        if (formDetails) {

            const sectorValid = formDetails.Sector !== null;
            const languageValid = formDetails.Language.Id !== 0;
            const organizationValid = formDetails.Name.length > 0;
            const emailValid = formDetails.ContactEmail.length > 0 && emailStringValidation(formDetails.ContactEmail);
            const homeCountryValid = formDetails.Country.Id != 0;
            const stateValid = states.length > 1 ? formDetails.State.Id !== 0 : true;

            const errors: string[] = [];
            if (!organizationValid) errors.push(formatMessage({ id: 'organization_is_required.bc24b3d1', defaultMessage: 'Organization is required' }));
            if (!emailValid) {

                errors.push(formDetails.ContactEmail.length === 0
                    ? formatMessage({ id: 'corporate_email_is_required.3452cf0b', defaultMessage: 'Corporate email is required' })
                    : formatMessage({ id: 'please_enter_a_valid_email_address.9e89059d', defaultMessage: 'Please enter a valid email address' }));

            }
            if (!sectorValid) errors.push(formatMessage({ id: 'sector_is_required.461eda94', defaultMessage: 'Sector is required' }));
            if (!languageValid) errors.push(formatMessage({ id: 'language_is_required.e4e4fde8', defaultMessage: 'Language is required' }));
            if (!homeCountryValid) errors.push(formatMessage({ id: 'home_country_is_required.ec5d23be', defaultMessage: 'Home Country is required' }));
            if (!stateValid) errors.push(formatMessage({ id: 'stateprovince_is_required.107b9aa7', defaultMessage: 'State/Province is required' }));

            setState({ contactEmailIsValid: emailValid, homeCountryIsValid: homeCountryValid, languageIsValid: languageValid, organizationIsValid: organizationValid, sectorIsValid: sectorValid, stateIsValid: stateValid });

            if (errors.length > 0) {

                createToast(errors.join('. ') + '.', 'danger');
                return false;

            }
            return true;

        }

    };

    const handleCreatePartnerClick = async () => {

        if (validateNewPartnerDetails() && selectedLicenses.length > 0, formDetails) {

            const partnerCreated = await PartnerApi.Create(formDetails).catch(catchWithToast(formatMessage({ id: 'failed_to_create_partner.73437231', defaultMessage: 'Failed to create partner.' })));
            if (partnerCreated && partnerCreated.Done && partnerCreated.Id) {

                await PartnerApi.AddLicensesToPartner(
                    partnerCreated.Id,
                    selectedLicenses.map(license => license.Id),
                    selectedLicenses.map(license => license.SuppliedBy)
                ).catch(catchWithToast(formatMessage({ id: 'failed_to_add_licenses_to_partner.c8f99849', defaultMessage: 'Failed to add licenses to partner.' })));
                const partner = await PartnerApi.Get(partnerCreated.Id).catch(catchWithToast(formatMessage({ id: 'failed_to_load_partner_details.bf4dc424', defaultMessage: 'Failed to load partner details.' })));
                if (partner) {

                    return partner;

                }
                return null;

            }

        }

    };

    const updateLicenseSupplier = (supplier: SupplierOption, licenseId: number) => {

        const newSetOfLicenses = [...licenses];
        const licenseIndex = newSetOfLicenses.findIndex(license => license.Id === licenseId);
        newSetOfLicenses[licenseIndex].SuppliedBy = supplier.Id;
        newSetOfLicenses[licenseIndex].SuppliedByName = supplier.Name;
        setState({ licenses: newSetOfLicenses });

    };

    const filterOutActiveCountries = (operatingCountries: Country[]) => countries.filter(country => {

        if (operatingCountries && operatingCountries.length > 0) {

            const hasOperatingCountry = operatingCountries.find(opCountry => opCountry.Id == country.Id);

            if (!hasOperatingCountry) {

                return country;

            }

        } else {

            return country;

        }

    }).filter(country => country.Id !== details.Country.Id);

    return (
        <>
            <ContentWrapper style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                <div className="my-5">
                    <GMetrixStepper
                        handleChange={e => handleStepperChange(e.value)}
                        linear={false}
                        setStep={val => setState({ step: val })}
                        step={step}
                        stepItems={stepItems}
                    />
                </div>

                {step === 0 && formDetails && (
                    <>
                        <Row>
                            <Col className="mb-3" md={6} sm={12}>
                                <GMetrixInput
                                    readOnly
                                    label={formatMessage({ id: 'type.b65913c1', defaultMessage: 'Type', description: 'Formulario' })}
                                    value={partnerTypeNames[formDetails.PartnerType - 1]}
                                />
                            </Col>
                            <Col className="mb-3" md={6} sm={12}>
                                <GMetrixCheckInput
                                    checked={formDetails.Active}
                                    label={formatMessage({ id: 'active.b9616c88', defaultMessage: 'Active' })}
                                    onChange={e => setState({ formDetails: { ...formDetails, Active: e.target.checked } })}
                                    readOnly={!permissions.Edit}
                                    type="switch"
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col className="mb-3" md={6} sm={12}>
                                <GMetrixInput
                                    required
                                    label={formatMessage({ id: 'organization.7cb537a9', defaultMessage: 'Organization', description: 'New' })}
                                    onBlur={() => setState({ organizationIsValid: formDetails.Name.length > 0 })}
                                    onChange={e => setState({ formDetails: { ...formDetails, Name: e.target.value } })}
                                    readOnly={!permissions.Edit}
                                    value={formDetails.Name}
                                />
                            </Col>
                            <Col className="mb-3" md={6} sm={12}>
                                <GMetrixInput
                                    required
                                    onBlur={() => setState({
                                        contactEmailIsValid:
                                            formDetails.ContactEmail.length > 0 &&
                                            emailStringValidation(formDetails.ContactEmail)
                                    })
                                    }
                                    label={formatMessage({ id: 'corporate_email.a3823c74', defaultMessage: 'Corporate Email', description: 'New' })}
                                    onChange={e => setState({ formDetails: { ...formDetails, ContactEmail: e.target.value } })}
                                    readOnly={!permissions.Edit}
                                    value={formDetails.ContactEmail}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col className="mb-3" md={6} sm={12}>
                                <GMetrixDropdown
                                    required
                                    onChange={dataItem => {

                                        setState({ formDetails: { ...formDetails, Sector: dataItem.Id }, sectorIsValid: dataItem !== null });

                                    }}
                                    value={{
                                        Id: formDetails.Sector,
                                        Name: sectors.find(sector => sector.Id === formDetails.Sector)?.Name
                                    }}
                                    dataItemKey="Id"
                                    dataItems={sectors}
                                    disabled={!permissions.Edit}
                                    isValid={sectorIsValid}
                                    label={formatMessage({ id: 'sector.343b1273', defaultMessage: 'Sector', description: 'New' })}
                                    textField="Name"
                                />
                            </Col>
                            <Col className="mb-3" md={6} sm={12}>
                                <GMetrixDropdown
                                    required
                                    onChange={dataItem => {

                                        setState({ formDetails: { ...formDetails, Language: dataItem }, languageIsValid: dataItem.Id !== 0 });

                                    }}
                                    dataItemKey="Id"
                                    dataItems={languages}
                                    disabled={!permissions.Edit}
                                    isValid={languageIsValid}
                                    label={formatMessage({ id: 'language.bc205d83', defaultMessage: 'Language' })}
                                    textField="Name"
                                    value={formDetails.Language}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col className="mb-3" md={6} sm={12}>
                                <GMetrixInput
                                    label={formatMessage({ id: 'customer_id.16ab92c3', defaultMessage: 'Customer ID', description: 'New' })}
                                    onChange={e => setState({ formDetails: { ...formDetails, CustomerIds: e.target.value } })}
                                    readOnly={!permissions.Edit || highestRole.partnerId === PartnerTypes.SuperAdmin}
                                    value={formDetails.CustomerIds}
                                />
                            </Col>
                            <Col className="mb-3" md={6} sm={12}>
                                <GMetrixInput
                                    label={formatMessage({ id: 'phone.dd52f414', defaultMessage: 'Phone', description: 'New' })}
                                    onChange={e => setState({ formDetails: { ...formDetails, Phone: e.target.value } })}
                                    readOnly={!permissions.Edit}
                                    value={formDetails.Phone}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col className="mb-3" md={6} sm={12}>
                                <GMetrixDropdown
                                    required
                                    onChange={async value => {

                                        const dataItem = value as Country;
                                        setState({ formDetails: { ...state.formDetails, Country: dataItem, State: { Id: 0, Name: '' } }, homeCountryIsValid: dataItem.Id !== 0 });
                                        try {

                                            const fetchedStates = await StateApi.GetAll(dataItem.Id);
                                            setStates(fetchedStates);
                                            if (fetchedStates.length === 0) {

                                                setState({ formDetails: { ...state.formDetails, State: { Id: 219, Name: formatMessage({ id: 'not_applicable.a01fc143', defaultMessage: 'Not Applicable' }) } } });

                                            }

                                        } catch (err) {

                                            catchWithToast(formatMessage({ id: 'failed_to_load_states.ec2a3c83', defaultMessage: 'Failed to load states.' }))(err);

                                        }

                                    }}
                                    dataItemKey="Id"
                                    dataItems={countries}
                                    disabled={!permissions.Edit}
                                    isValid={homeCountryIsValid}
                                    label={formatMessage({ id: 'home_country.829daf61', defaultMessage: 'Home Country' })}
                                    textField="Name"
                                    value={formDetails.Country}
                                />
                            </Col>
                            <Col className="mb-3" md={6} sm={12}>
                                <GMetrixDropdown
                                    dataItemKey="Id"
                                    dataItems={states}
                                    disabled={!permissions.Edit || states.length <= 1 || formDetails.State?.Id === 219}
                                    isValid={stateIsValid}
                                    label={formatMessage({ id: 'stateprovince.d2dfa3c8', defaultMessage: 'State/Province' })}
                                    onChange={dataItem => setState({ formDetails: { ...formDetails, State: dataItem } })}
                                    required={states.length > 0 && formDetails.State?.Id !== 219}
                                    textField="Name"
                                    value={formDetails.State}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col className="mb-3" md={6} sm={12}>
                                <GMetrixInput
                                    label={formatMessage({ id: 'address_line_1.25dd7c0d', defaultMessage: 'Address Line 1' })}
                                    onChange={e => setState({ formDetails: { ...formDetails, Address1: e.target.value } })}
                                    readOnly={!permissions.Edit}
                                    value={formDetails.Address1}
                                />
                            </Col>
                            <Col className="mb-3" md={6} sm={12}>
                                <GMetrixInput
                                    label={formatMessage({ id: 'address_line_2.74e16278', defaultMessage: 'Address Line 2' })}
                                    onChange={e => setState({ formDetails: { ...formDetails, Address2: e.target.value } })}
                                    readOnly={!permissions.Edit}
                                    value={formDetails.Address2}

                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col className="mb-3" md={6} sm={12}>
                                <GMetrixInput
                                    label={formatMessage({ id: 'city.e7690187', defaultMessage: 'City' })}
                                    onChange={e => setState({ formDetails: { ...formDetails, City: e.target.value } })}
                                    readOnly={!permissions.Edit}
                                    value={formDetails.City}
                                />
                            </Col>
                            <Col className="mb-3" md={6} sm={12}>
                                <GMetrixInput
                                    label={formatMessage({ id: 'zippostal_code.9631e317', defaultMessage: 'Zip/Postal Code' })}
                                    onChange={e => setState({ formDetails: { ...formDetails, Zip: e.target.value } })}
                                    readOnly={!permissions.Edit}
                                    value={formDetails.Zip}
                                />
                            </Col>
                            {(partner.type === PartnerTypes.Distributor || partner.type === PartnerTypes.Reseller)
                                && (
                                    <Col className="mb-3" md={6} sm={12}>
                                        <GMetrixComboBoxList
                                            setDataList={e => {

                                                setState({ currentOperatingCountryList: e, formDetails: { ...formDetails, OperatingCountries: e } });

                                            }}
                                            dataList={operatingCountries}
                                            disabled={!permissions.Edit}
                                            displayNameProp={'Name'}
                                            label={formatMessage({ id: 'operating_country.6b69febd', defaultMessage: 'Operating Country' })}
                                            selectedListItems={currentOperatingCountryList}
                                        />
                                    </Col>
                                )}
                        </Row>
                    </>
                )}
                {step === 1 && licenses.length > 0 && (
                    <Row>
                        <Col className="mb-3" xs={12}>
                            <LicenseGMetrixTable
                                manuallyRenderLicenses
                                selectableRows
                                columnData={[
                                    { field: 'Id', title: formatMessage({ id: 'license_id.87d2b466', defaultMessage: 'License ID' }) },
                                    { field: 'Name', title: formatMessage({ id: 'name.5458f379', defaultMessage: 'Name' }) },
                                    { field: 'LicenseTypeString', title: formatMessage({ id: 'type.b65913c1', defaultMessage: 'Type', description: 'Formulario' }) },
                                    { field: 'MaxUses', title: formatMessage({ id: 'max_uses.5571019d', defaultMessage: 'Max Uses' }) },
                                    { field: 'UsageDays', title: formatMessage({ id: 'usage_days.d9750693', defaultMessage: 'Usage Days', description: 'Formulario' }) },
                                    {
                                        cell: ({ dataItem }) => (
                                            <>
                                                {dataItem.SupplierOptions.length > 1 ? (
                                                    <SuppliedByDropdown
                                                        currentSupplier={{ Id: dataItem.SuppliedBy, Name: dataItem.SuppliedByName, Type: parseInt(dataItem.LicenseType) }}
                                                        license={dataItem}
                                                        licenses={licenses} setLicenses={val => setState({ licenses: val })}
                                                        supplierOptions={dataItem.SupplierOptions}
                                                        updateLicenseSupplier={updateLicenseSupplier}
                                                    />
                                                ) : (
                                                    <div className="p-2">
                                                        {dataItem.SuppliedByName}
                                                    </div>
                                                )}
                                            </>

                                        ),
                                        field: 'SuppliedByName',
                                        title: formatMessage({ id: 'supplier.a310b248', defaultMessage: 'Supplier' }),
                                        width: 200
                                    }
                                ]}
                                advancedSearchFields={licenseDefaultLookupSearchFields}
                                className="p-0"
                                licenses={licenses}
                                loadingGridData={loadingGridData}
                                onSelectedChange={val => setState({ selectedLicenses: val })}
                                searchTerm={searchTerm}
                                selectedLicenses={selectedLicenses}
                                setLicenses={val => setState({ licenses: val })}
                                setLoadingGridData={val => setState({ loadingGridData: val })}
                                setSearchTerm={val => setState({ searchTerm: val })}
                            />
                        </Col>
                    </Row>
                )}

            </ContentWrapper>
            <ActionsWrapper>
                {onClose && (
                    <GMetrixButton className="mx-2" onClick={onClose} variant="secondary">
                        <FormattedMessage defaultMessage="Cancel" id="cancel.89a86adc" />
                    </GMetrixButton>
                )}
                <GMetrixButton onClick={handleStepperChange} variant="primary">
                    {step === PartnerCreateStep.Details ? <FormattedMessage defaultMessage="Next" id="next.50b7834e" /> : <FormattedMessage defaultMessage="Create" id="create.be56b47d" />}
                </GMetrixButton>
            </ActionsWrapper>
        </>
    );

};
